// The Requests tab of the receiving hub: things people asked for that were
// bought and have not arrived, and the answer to a shipping label scanned into
// the hub's scan box.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '../components/table/DataTable';
import { useToast } from '../components/Toast/ToastContext';
import { PackagePips } from '../Requisitions/PackagesCard';
import { buttonClass } from '../components/ui/Button';
import { Field } from '../components/ui/Field';
import { Tooltip } from '../components/ui/Tooltip';
import { Alert } from '../components/ui/Alert';
import FadeIn from '../components/ui/FadeIn';
import { EmptyState } from '../components/ui/EmptyState';
import { LogoSpinner } from '../components/ui/LogoSpinner';
import { ListRow, ListStack } from '../components/ui/ListRow';
import { Panel } from '../components/ui/Panel';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';
import {
  CARRIER_LABELS,
  errorMessage,
  packageCount,
  useRequisitionsApi,
  type RequisitionDetail,
  type RequisitionSummary,
  type ScanMatch,
  type Shipment,
} from '../Requisitions/requisitionsApi';

export interface RequestScan {
  raw: string;
  /** Bumped per scan, so the same label scanned twice is looked up twice. */
  nonce: number;
}

interface Props {
  scan: RequestScan | null;
  /**
   * Called as soon as a scan is looked up. The hub drops it then, or a tab
   * switch away and back would remount this and post the same label again.
   */
  onScanTaken: () => void;
}

type Result =
  | { kind: 'looking'; raw: string }
  | { kind: 'match'; raw: string; match: ScanMatch }
  | { kind: 'arrived'; match: ScanMatch; detail: RequisitionDetail }
  | { kind: 'matches'; raw: string; matches: ScanMatch[] }
  | { kind: 'miss'; raw: string; missId: string }
  | { kind: 'found'; raw: string; missId: string; detail: RequisitionDetail }
  | { kind: 'failed'; raw: string };

interface RowMeta {
  confirmingId: string | null;
  busyId: string | null;
  /** Set after a scan that matched nothing: Arrived then asks which package. */
  missId: string | null;
  setConfirmingId: (id: string | null) => void;
  onArrive: (row: RequisitionSummary) => void;
  onPick: (row: RequisitionSummary) => void;
}

const SMALL_PRIMARY = buttonClass('filled', 'sm');
const SMALL_TEXT = buttonClass('quiet', 'sm');
const BTN_ROW = buttonClass('quiet', 'sm');
const BTN_EMPTY = buttonClass('tonal', 'md');
const BTN_PRIMARY = buttonClass('filled', 'md');

const getRowId = (r: RequisitionSummary) => r.id;

// Module scope, per the DataTable contract; the per-render state rides `meta`.
const COLUMNS: ColumnDef<RequisitionSummary, any>[] = [
  {
    id: 'req_number',
    header: 'Request',
    meta: { cellClass: 'whitespace-nowrap' },
    cell: ({ row }) => {
      const toStock = row.original.lines_to_stock ?? 0;
      return (
        <div className="flex flex-col items-start gap-0.5">
          <Link
            to={`/requisitions/${row.original.id}`}
            className="font-mono text-sm text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded"
          >
            {row.original.req_number}
          </Link>
          {toStock > 0 && (
            <Tooltip
              label={
                toStock === 1
                  ? 'One item on it goes into stock. Receive it from the request.'
                  : `${toStock} items on it go into stock. Receive them from the request.`
              }
            >
              <Link
                to={`/requisitions/${row.original.id}`}
                className="rounded text-xs text-on-surface-variant hover:text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
              >
                Receive into stock
              </Link>
            </Tooltip>
          )}
        </div>
      );
    },
  },
  {
    id: 'purpose',
    header: 'What for',
    meta: {
      cellClass: 'max-w-[22rem] truncate text-on-surface',
      cellAttrs: (r: RequisitionSummary) => ({ title: r.purpose }),
    },
    cell: ({ row }) => row.original.purpose,
  },
  {
    id: 'requester',
    header: 'Asked by',
    meta: { cellClass: 'whitespace-nowrap text-on-surface-variant' },
    cell: ({ row }) => row.original.requester?.name || '—',
  },
  {
    id: 'stores',
    header: 'Store',
    meta: {
      cellClass: 'max-w-[12rem] truncate text-on-surface-variant',
      cellAttrs: (r: RequisitionSummary) => ({ title: (r.stores || []).join(', ') }),
    },
    cell: ({ row }) => (row.original.stores && row.original.stores.length ? row.original.stores.join(', ') : '—'),
  },
  {
    id: 'packages',
    header: 'Packages',
    meta: { cellClass: 'whitespace-nowrap text-on-surface-variant' },
    cell: ({ row }) => {
      const total = row.original.shipments_total;
      const arrived = row.original.shipments_arrived ?? 0;
      if (total === undefined) return '—';
      if (total === 0) return 'No tracking';
      return (
        <span className="inline-flex items-center gap-2 tabular-nums">
          <PackagePips arrived={arrived} total={total} />
          {arrived === total ? 'All packages in' : `${arrived} of ${total}`}
        </span>
      );
    },
  },
  {
    id: 'arrive',
    header: () => <span className="sr-only">Mark arrived</span>,
    meta: { align: 'right', cellClass: 'whitespace-nowrap' },
    cell: (info) => {
      const r = info.row.original;
      const m = info.table.options.meta as unknown as RowMeta;
      const who = r.requester?.name || 'the person who asked';
      // An arrived row is here only for its stock; the hint under its number says so.
      if (r.status === 'arrived') return null;
      if (m.missId && (r.shipments_total ?? 0) > 0) {
        return (
          <Tooltip label="Pick which package came in">
            <button type="button" disabled={m.busyId !== null} onClick={() => m.onPick(r)} className={BTN_ROW}>
              {m.busyId === r.id ? 'Opening…' : 'Arrived'}
            </button>
          </Tooltip>
        );
      }
      // Marking the request arrived is refused until a buyer picks the item.
      if ((r.lines_need_item ?? 0) > 0) {
        return <span className="text-xs text-on-surface-variant">Waiting on a catalog item</span>;
      }
      // Two steps in place: the confirm lands where the pointer already is, and
      // a popover would be clipped by the table's own scroll box.
      if (m.confirmingId === r.id) {
        return (
          <span className="inline-flex items-center gap-1">
            <button
              type="button"
              disabled={m.busyId !== null}
              onClick={() => m.onArrive(r)}
              className={SMALL_PRIMARY}
            >
              {m.busyId === r.id ? 'Saving…' : 'Mark arrived'}
            </button>
            <button type="button" onClick={() => m.setConfirmingId(null)} className={SMALL_TEXT}>
              Never mind
            </button>
          </span>
        );
      }
      const allIn = (r.shipments_total ?? 0) > 0 && r.shipments_arrived === r.shipments_total;
      return (
        <Tooltip
          label={
            allIn
              ? `Every package is in. Marks it arrived and tells ${who}, once each item going into stock has a catalog item`
              : `Marks every package on it arrived and tells ${who}`
          }
        >
          <button
            type="button"
            disabled={m.busyId !== null}
            onClick={() => m.setConfirmingId(r.id)}
            className={BTN_ROW}
          >
            Arrived
          </button>
        </Tooltip>
      );
    },
  },
];

const PulseRows: React.FC = () => (
  <SkeletonGroup label="Loading requests on the way" className="space-y-2 px-4 py-4">
    {[0, 1, 2, 3].map((i) => (
      <Skeleton key={i} className="h-8" />
    ))}
  </SkeletonGroup>
);

/** The row as the server now has it, or gone when nothing on it is left for receiving. */
function afterArrival(prev: RequisitionSummary[], detail: RequisitionDetail): RequisitionSummary[] {
  if (detail.status === 'arrived' && !(detail.lines_to_stock && detail.lines_to_stock > 0)) {
    return prev.filter((x) => x.id !== detail.id);
  }
  return prev.map((x) => (x.id === detail.id ? { ...x, ...detail } : x));
}

function matchesQuery(r: RequisitionSummary, q: string): boolean {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  return [r.req_number, r.requester?.name || '', r.purpose].some((v) => v.toLowerCase().includes(needle));
}

const RequestsTab: React.FC<Props> = ({ scan, onScanTaken }) => {
  const reqApi = useRequisitionsApi();
  const toast = useToast();
  const [rows, setRows] = useState<RequisitionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [result, setResult] = useState<Result | null>(null);
  const [query, setQuery] = useState('');
  const [note, setNote] = useState('');
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const scanSeq = useRef(0);

  const load = async () => {
    setLoading(true);
    setFailed(false);
    try {
      setRows(await reqApi.list('on_the_way'));
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!scan) return;
    onScanTaken();
    const seq = (scanSeq.current += 1);
    setResult({ kind: 'looking', raw: scan.raw });
    setNote('');
    setQuery('');
    reqApi
      .scan(scan.raw)
      .then((res) => {
        if (seq !== scanSeq.current) return;
        if (res.kind === 'match') setResult({ kind: 'match', raw: scan.raw, match: res.match });
        else if (res.kind === 'matches') setResult({ kind: 'matches', raw: scan.raw, matches: res.matches });
        else setResult({ kind: 'miss', raw: scan.raw, missId: res.missId });
      })
      .catch(() => {
        if (seq === scanSeq.current) setResult({ kind: 'failed', raw: scan.raw });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scan?.nonce]);

  const missId = result?.kind === 'miss' || result?.kind === 'found' ? result.missId : undefined;

  const arriveRequest = async (r: RequisitionSummary) => {
    setBusyId(r.id);
    try {
      const detail = await reqApi.markArrived(r.id, undefined, missId || undefined);
      setRows((prev) => afterArrival(prev, detail));
      setConfirmingId(null);
      if (missId) setResult(null);
      toast.success(`Marked ${r.req_number} arrived.`);
    } catch (err) {
      toast.error(errorMessage(err, 'That did not go through. Check your connection and try again.'));
      load();
    } finally {
      setBusyId(null);
    }
  };

  const arrivePackage = async (match: ScanMatch, scanMissId?: string) => {
    setBusyId(match.shipment.id);
    try {
      const detail = await reqApi.markShipmentArrived(match.requisition.id, match.shipment.id, note, scanMissId);
      setResult({ kind: 'arrived', match, detail });
      setNote('');
      setRows((prev) => afterArrival(prev, detail));
      document.getElementById('box-barcode')?.focus();
    } catch (err) {
      toast.error(errorMessage(err, 'That did not go through. Check your connection and try again.'));
    } finally {
      setBusyId(null);
    }
  };

  // After a miss, the request found by hand opens with its packages, so the
  // one in hand is marked rather than the whole request.
  const pickRequest = async (r: RequisitionSummary) => {
    if (!result || (result.kind !== 'miss' && result.kind !== 'found')) return;
    const { raw, missId: id } = result;
    setBusyId(r.id);
    try {
      const detail = await reqApi.get(r.id);
      setNote('');
      setResult({ kind: 'found', raw, missId: id, detail });
    } catch (err) {
      toast.error(errorMessage(err, 'Could not open that request. Check your connection and try again.'));
    } finally {
      setBusyId(null);
    }
  };

  const meta: RowMeta = useMemo(
    () => ({
      confirmingId,
      busyId,
      missId: missId || null,
      setConfirmingId,
      onArrive: arriveRequest,
      onPick: pickRequest,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [confirmingId, busyId, missId, result]
  );

  const shown = rows.filter((r) => matchesQuery(r, query));
  const dismiss = (
    <button type="button" onClick={() => setResult(null)} className={SMALL_TEXT}>
      Dismiss
    </button>
  );

  return (
    <section aria-labelledby="requests-heading" className="space-y-3">
      <div aria-live="polite">
        <FadeIn key={result?.kind ?? 'none'}>
        {result?.kind === 'looking' && (
          <Alert tone="info" live={false} icon={<LogoSpinner size="inline" />}>
            Looking up <span className="break-all font-mono text-on-surface">{result.raw}</span>…
          </Alert>
        )}

        {result?.kind === 'match' && (
          <Panel level={3} className="ring-1 ring-primary/30" title={<MatchTitle match={result.match} />}>
            <MatchSub match={result.match} />
            <form
              onSubmit={(e) => {
                e.preventDefault();
                arrivePackage(result.match);
              }}
              className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center"
            >
              <label htmlFor="scan-arrive-note" className="sr-only">
                Where is it? (optional)
              </label>
              <Field
                id="scan-arrive-note"
                thumb
                value={note}
                maxLength={500}
                placeholder="Where is it? (optional)"
                onChange={(e) => setNote(e.target.value)}
                className="sm:max-w-sm"
              />
              <div className="flex items-center gap-2">
                <Tooltip label="Marks this package arrived. The last one marks the whole request arrived.">
                  <button type="submit" disabled={busyId !== null} className={BTN_PRIMARY}>
                    {busyId === result.match.shipment.id ? 'Saving…' : 'Mark arrived'}
                  </button>
                </Tooltip>
                {dismiss}
              </div>
            </form>
          </Panel>
        )}

        {result?.kind === 'arrived' && <ArrivedCard match={result.match} detail={result.detail} dismiss={dismiss} />}

        {result?.kind === 'matches' && (
          <Panel
            level={3}
            className="ring-1 ring-primary/30"
            title={`This label fits ${result.matches.length} requests`}
            actions={dismiss}
          >
            <p className="mb-3 text-sm text-on-surface-variant">Pick the one whose package you are holding.</p>
            <ListStack
              items={result.matches}
              getKey={(m) => `${m.requisition.id}-${m.shipment.id}`}
              on="panel"
              renderRow={(m) => (
                <ListRow onClick={() => setResult({ kind: 'match', raw: result.raw, match: m })}>
                  <span className="flex min-w-0 items-center gap-3">
                    <span className="font-mono text-sm text-primary">{m.requisition.req_number}</span>
                    <span className="min-w-0 flex-1 truncate text-sm text-on-surface">{m.requisition.purpose}</span>
                    <span className="whitespace-nowrap text-xs text-on-surface-variant">
                      {m.requisition.requester?.name || ''}
                    </span>
                  </span>
                </ListRow>
              )}
            />
          </Panel>
        )}

        {result?.kind === 'found' && (
          <FoundCard
            result={result}
            note={note}
            setNote={setNote}
            busyId={busyId}
            onPackage={(shipment) => arrivePackage({ requisition: result.detail, shipment }, result.missId)}
            onAll={() => arriveRequest(result.detail)}
            onBack={() => setResult({ kind: 'miss', raw: result.raw, missId: result.missId })}
          />
        )}

        {(result?.kind === 'miss' || result?.kind === 'failed') && (
          <Panel
            level={3}
            title={(
              <>
                {result.kind === 'miss' ? 'No request matches ' : 'Could not check '}
                <span className="break-all font-mono">{result.raw}</span>
              </>
            )}
            actions={dismiss}
          >
            <p className="text-sm text-on-surface-variant">
              {result.kind === 'miss'
                ? 'Find it below by request number or name, then press Arrived.'
                : 'Check your connection and scan it again, or find it below by request number or name.'}
            </p>
            <label htmlFor="requests-find" className="sr-only">
              Find a request by number or name
            </label>
            <Field
              id="requests-find"
              thumb
              value={query}
              autoComplete="off"
              placeholder="REQ-0012, or who asked"
              onChange={(e) => setQuery(e.target.value)}
              className="mt-3 sm:max-w-sm"
            />
          </Panel>
        )}
        </FadeIn>
      </div>

      <DataTable<RequisitionSummary>
        data={shown}
        columns={COLUMNS}
        getRowId={getRowId}
        meta={meta}
        stickyHeader
        strip={
          <h2 id="requests-heading" className="text-sm font-semibold text-on-surface">
            Requests on the way
          </h2>
        }
        loading={loading}
        loadingContent={<PulseRows />}
        empty={
          failed ? (
            <EmptyState
              variant="area"
              title="Could not load the requests on the way."
              action={(
                <button type="button" onClick={load} className={BTN_EMPTY}>
                  Try again
                </button>
              )}
            >
              Check your connection, then try again.
            </EmptyState>
          ) : query.trim() ? (
            <EmptyState
              variant="area"
              action={(
                <button type="button" onClick={() => setQuery('')} className={BTN_EMPTY}>
                  Clear the search
                </button>
              )}
            >
              Nothing on the way matches “{query.trim()}”.
            </EmptyState>
          ) : (
            <EmptyState variant="area" title="Nothing on the way.">
              Requests show up here once a buyer marks them bought. Scan a shipping label in the box
              above to find the one it belongs to.
            </EmptyState>
          )
        }
      />
    </section>
  );
};

const MatchTitle: React.FC<{ match: ScanMatch }> = ({ match }) => {
  const r = match.requisition;
  return (
    <>
      <Link to={`/requisitions/${r.id}`} className="font-mono text-primary hover:underline">
        {r.req_number}
      </Link>{' '}
      <span className="break-words">{r.purpose}</span>
    </>
  );
};

const MatchSub: React.FC<{ match: ScanMatch }> = ({ match }) => {
  const r = match.requisition;
  const s = match.shipment;
  return (
    <p className="text-sm text-on-surface-variant">
      {r.requester ? `Asked by ${r.requester.name}. ` : ''}
      {CARRIER_LABELS[s.carrier] || CARRIER_LABELS.other} package{' '}
      <span className="break-all font-mono text-on-surface">{s.tracking_number}</span>
    </p>
  );
};

const FoundCard: React.FC<{
  result: Extract<Result, { kind: 'found' }>;
  note: string;
  setNote: (v: string) => void;
  busyId: string | null;
  onPackage: (shipment: Shipment) => void;
  onAll: () => void;
  onBack: () => void;
}> = ({ result, note, setNote, busyId, onPackage, onAll, onBack }) => {
  const r = result.detail;
  const open = (r.shipments || []).filter((s) => !s.arrived_at);
  return (
    <Panel
      level={3}
      className="ring-1 ring-primary/30"
      title={(
        <>
          <Link to={`/requisitions/${r.id}`} className="font-mono text-primary hover:underline">
            {r.req_number}
          </Link>{' '}
          <span className="break-words">{r.purpose}</span>
        </>
      )}
      actions={(
        <button type="button" onClick={onBack} className={SMALL_TEXT}>
          Not this one
        </button>
      )}
    >
      <p className="text-sm text-on-surface-variant">
        {open.length
          ? 'Which package is in your hands? The label you scanned gets filed under this request.'
          : 'No package is still open on this one.'}
      </p>
      <label htmlFor="found-arrive-note" className="sr-only">
        Where is it? (optional)
      </label>
      <Field
        id="found-arrive-note"
        thumb
        value={note}
        maxLength={500}
        placeholder="Where is it? (optional)"
        onChange={(e) => setNote(e.target.value)}
        className="mt-3 sm:max-w-sm"
      />
      {open.length > 0 && (
        <ListStack
          className="mt-3"
          items={open}
          getKey={(s) => s.id}
          on="panel"
          renderRow={(s) => (
            <ListRow
              actions={(
                <button type="button" disabled={busyId !== null} onClick={() => onPackage(s)} className={SMALL_PRIMARY}>
                  {busyId === s.id ? 'Saving…' : 'Arrived'}
                </button>
              )}
            >
              <span className="flex min-w-0 items-center gap-3">
                <span className="whitespace-nowrap text-xs text-on-surface-variant">
                  {CARRIER_LABELS[s.carrier] || CARRIER_LABELS.other}
                </span>
                <span className="min-w-0 flex-1 break-all font-mono text-sm text-on-surface">{s.tracking_number}</span>
              </span>
            </ListRow>
          )}
        />
      )}
      <div className="mt-3">
        <Tooltip label="Marks every package on it arrived">
          <button type="button" disabled={busyId !== null} onClick={onAll} className={open.length ? buttonClass('quiet', 'md') : BTN_PRIMARY}>
            {busyId === r.id ? 'Saving…' : 'All of it arrived'}
          </button>
        </Tooltip>
      </div>
    </Panel>
  );
};

const ArrivedCard: React.FC<{ match: ScanMatch; detail: RequisitionDetail; dismiss: React.ReactNode }> = ({
  match,
  detail,
  dismiss,
}) => {
  const { arrived, total } = packageCount(detail.shipments || []);
  const who = detail.requester?.name || 'The person who asked';
  const toStock = detail.lines_to_stock ?? 0;
  let said: string;
  if (detail.status === 'arrived') said = `All packages in. ${who} hears about it.`;
  else if (arrived === total) {
    said = `All packages in. It stays open until a buyer picks the catalog item for a line going into stock. ${who} hears about this package.`;
  } else said = `Package in, ${arrived} of ${total}. ${who} hears about it.`;
  return (
    <Panel level={3} title={<MatchTitle match={match} />} actions={dismiss}>
      <MatchSub match={match} />
      <p className="mt-3 inline-flex flex-wrap items-center gap-2 text-sm text-on-surface">
        <svg aria-hidden="true" viewBox="0 0 16 16" fill="none" className="h-4 w-4 text-emerald-300">
          <path
            fill="currentColor"
            d="M13.28 4.22a.75.75 0 0 1 0 1.06l-6.5 6.5a.75.75 0 0 1-1.06 0l-3-3a.75.75 0 1 1 1.06-1.06l2.47 2.47 5.97-5.97a.75.75 0 0 1 1.06 0Z"
          />
        </svg>
        {said}
        <PackagePips arrived={arrived} total={total} landing />
      </p>
      {toStock > 0 && (
        <p className="mt-2 text-sm text-on-surface-variant">
          {toStock === 1 ? 'One item on it goes into stock. ' : `${toStock} items on it go into stock. `}
          <Link
            to={`/requisitions/${detail.id}`}
            className="rounded text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
          >
            Receive into stock
          </Link>
        </p>
      )}
    </Panel>
  );
};

export default RequestsTab;
